import classNames from "classnames";
import React from "react";
import PanelSegmentType from "../components/panels/PanelSegment/PanelSegment";
import ChecklistApprovedRowCard from "./items/ChecklistApprovedRowCard";
import ChecklistNotAnsweredRowCard from "./items/ChecklistNotAnsweredRowCard";
import ChecklistDeclinedRowCard from "./items/ChecklistDeclinedRowCard";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { onESignActivity, onInvoiceActivity } from "common/signalR";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { useCurrentUser } from "common/SessionContext";

interface Props {
  id: string
  checklistItems: server.dto.DocumentChecklistItem[]
  onChecklistItemsChanged: (checklistItems: server.dto.DocumentChecklistItem[]) => void
  hasTriedSubmitting: boolean
  documentId: number
  setApi: typeof window.CoreApi.Checklist.SetESignChecklistItemState
  whoCanReviewApi: typeof window.CoreApi.Checklist.WhoCanReviewESignChecklistItem;
  listApi: typeof window.CoreApi.Checklist.ListESignChecklistItems;
}

const DocumentChecklistPanel = (props: Props) => {
  const currentUser = useCurrentUser();
  const checklistActions: Array<server.dto.ESignActions | server.dto.InvoiceActions> = [
    "checklistItemApproved",
    "checklistItemDeclined",
    "checklistItemAnswerCleared",
    "checklistItemDeleted"
  ];

  React.useEffect(() => {
    onESignActivity((activity: server.dto.ESignActivity) => {
      if (activity.itemId !== props.documentId) return;

      const iAmActor = (activity.actor?.userId === currentUser.userId);

      if ((iAmActor) || !checklistActions.includes(activity.action.value)) return;

      refreshContent();
    });

    onInvoiceActivity((activity: server.dto.InvoiceActivity) => {
      if (activity.itemId !== props.documentId) return;

      const iAmActor = (activity.actor?.userId === currentUser.userId);

      if ((iAmActor) || !checklistActions.includes(activity.action.value)) return;

      refreshContent();
    });
  }, [props.documentId]);

  const canAnswer = site.permissions.canReview;

  const error = props.hasTriedSubmitting && !props.checklistItems.every(i => i.state === "approved");

  const onStateSet = async (item: server.dto.DocumentChecklistItem, newState: server.dto.ChecklistItemStates, declinedReason?: string) => {
    await props.setApi({
      documentChecklistItemId: item.documentChecklistItemId,
      state: newState,
      declinedReason
    });

    refreshContent();
  };

  const onReset = async (item: server.dto.DocumentChecklistItem) => {
    if (!canAnswer) return;

    await props.setApi({
      documentChecklistItemId: item.documentChecklistItemId,
      state: "notSet",
      declinedReason: undefined
    });

    refreshContent();
  };

  const refreshContent = async () => {
    const newList = await props.listApi(props.documentId);
    if (newList.data.hasErrors) {
      return;
    }
    props.onChecklistItemsChanged(newList.data.data);
  };

  const itemsContainerClass = classNames("list-with-gaps-body", {
    s1_warningborder: error
  });

  return <SectionPanel.Panel
    id={props.id}
    header={<>
      <PanelSegmentType.Title title={RESX.Checklist.resxChecklist} />
      <PanelSegmentType.SupportQuestionMark articleId={serverReference.supportArticleIds.whatIsAChecklist} />
    </>}
  >
    {(props.checklistItems.length === 0) && <NoDataAvailableDiv />}

    {(props.checklistItems.length > 0) && <div className="list-with-gaps card-layout">
      <div className={itemsContainerClass}>
        {props.checklistItems.map(item => {
          switch (item.state) {
            case "notSet": return <ChecklistNotAnsweredRowCard
              key={item.documentChecklistItemId}
              item={item}
              canAnswer={canAnswer}
              onAnswerSelected={async (newState, rejectionMsg) => onStateSet(item, newState, rejectionMsg)}
              whoCanReviewApi={props.whoCanReviewApi}
            />;
            case "declined": return <ChecklistDeclinedRowCard key={item.documentChecklistItemId} item={item} reset={async () => onReset(item)} />;
            case "approved": return <ChecklistApprovedRowCard key={item.documentChecklistItemId} item={item} reset={async () => onReset(item)} />;
            default: return null;
          }
        })}</div>
    </div>}
  </SectionPanel.Panel>
};

export default DocumentChecklistPanel;